/**
 * Phase 27.34 — Appointments block on the CRM Lead detail page.
 *
 * Lets any agent (KR admin or the assigned agent) schedule a call
 * for a lead that never came through a landing page. Same reschedule
 * / cancel affordances as the CRM Appointments dashboard, so the
 * agent can manage the appointment lifecycle without leaving the
 * lead profile.
 *
 * If the lead has no email, we still create the appointment but skip
 * the visitor confirmation email (the backend enforces the same rule).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatPhone } from '@/lib/utils';
import { toast } from 'sonner';
import {
  Calendar, Clock, Loader2, Plus, RefreshCw, XCircle, CheckCircle2,
  Mail, Phone, AlertTriangle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { appointmentsAPI } from '@/lib/api';
import DateTimeField from '@/components/common/DateTimeField';

const STATUS_META = {
  requested: { label: 'Requested', color: 'bg-slate-100 text-slate-700 border-slate-300' },
  confirmed: { label: 'Confirmed', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  rescheduled: { label: 'Rescheduled', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  cancelled: { label: 'Cancelled', color: 'bg-rose-100 text-rose-700 border-rose-300' },
  completed: { label: 'Completed', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  no_show: { label: 'No-show', color: 'bg-neutral-200 text-neutral-700 border-neutral-300' },
};

function formatLocal(iso, tz) {
  try {
    return new Date(iso).toLocaleString([], {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
      timeZone: tz || undefined,
    });
  } catch (_) {
    return iso;
  }
}

// Cheap browser timezone probe. Falls back to America/New_York so the
// server has a valid IANA name if the browser doesn't expose one.
function browserTz() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
      || 'America/New_York';
  } catch (_) {
    return 'America/New_York';
  }
}

export default function LeadAppointmentsCard({ lead, onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [rescheduling, setRescheduling] = useState(null);
  const [newIso, setNewIso] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await appointmentsAPI.list({ lead_id: lead.lead_id });
      setItems(r.data.appointments || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load appointments');
    } finally {
      setLoading(false);
    }
  }, [lead.lead_id]);

  useEffect(() => { load(); }, [load]);

  const activeItems = useMemo(
    () => items.filter((a) => a.status !== 'cancelled'),
    [items],
  );
  const cancelledItems = useMemo(
    () => items.filter((a) => a.status === 'cancelled'),
    [items],
  );

  const cancel = async (appt) => {
    if (!window.confirm(`Cancel this appointment on ${formatLocal(appt.scheduled_at, appt.timezone)}?`)) return;
    try {
      await appointmentsAPI.cancel(appt.appointment_id);
      toast.success('Appointment cancelled');
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cancel failed');
    }
  };

  const confirm = async (appt) => {
    try {
      await appointmentsAPI.confirm(appt.appointment_id);
      toast.success('Appointment confirmed');
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Confirm failed');
    }
  };

  const submitReschedule = async () => {
    if (!newIso) { toast.error('Pick a new date & time'); return; }
    try {
      await appointmentsAPI.reschedule(rescheduling.appointment_id, new Date(newIso).toISOString());
      toast.success('Appointment rescheduled');
      setRescheduling(null);
      setNewIso('');
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reschedule failed');
    }
  };

  return (
    <Card data-testid="lead-appointments-card">
      <CardHeader className="flex flex-row items-center justify-between py-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Calendar size={16} className="text-purple-600" />
          Appointments
          {items.length > 0 && (
            <Badge variant="outline" className="ml-1 font-normal">{items.length}</Badge>
          )}
        </CardTitle>
        <Button size="sm" onClick={() => setCreating(true)} data-testid="lead-appt-create-btn">
          <Plus size={14} className="mr-1" /> Create Appointment
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading ? (
          <div className="flex items-center gap-2 justify-center text-slate-500 py-6">
            <Loader2 className="animate-spin" size={16} /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="py-6 text-center text-sm text-slate-500 italic border border-dashed rounded-lg">
            No appointments yet. Click <span className="font-medium">Create Appointment</span> to schedule a call.
          </div>
        ) : (
          <>
            {activeItems.map((a) => (
              <AppointmentRow key={a.appointment_id} appt={a}
                onCancel={() => cancel(a)}
                onReschedule={() => {
                  setRescheduling(a);
                  const d = a.scheduled_at ? new Date(a.scheduled_at) : null;
                  if (d && !Number.isNaN(d.getTime())) {
                    const pad = (n) => String(n).padStart(2, '0');
                    setNewIso(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
                  } else {
                    setNewIso('');
                  }
                }}
                onConfirm={() => confirm(a)}
              />
            ))}
            {cancelledItems.length > 0 && (
              <details className="pt-2 border-t border-slate-100">
                <summary className="text-xs font-medium text-slate-500 cursor-pointer">
                  Cancelled ({cancelledItems.length})
                </summary>
                <div className="mt-2 space-y-2">
                  {cancelledItems.map((a) => (
                    <AppointmentRow key={a.appointment_id} appt={a} />
                  ))}
                </div>
              </details>
            )}
          </>
        )}
      </CardContent>

      <CreateAppointmentDialog
        open={creating}
        onOpenChange={setCreating}
        lead={lead}
        onCreated={async () => { setCreating(false); await load(); onChange?.(); }}
      />

      <RescheduleDialog
        open={!!rescheduling}
        onOpenChange={(v) => { if (!v) setRescheduling(null); }}
        appt={rescheduling}
        newIso={newIso}
        setNewIso={setNewIso}
        onSubmit={submitReschedule}
      />
    </Card>
  );
}

function AppointmentRow({ appt, onCancel, onReschedule, onConfirm }) {
  const meta = STATUS_META[appt.status] || STATUS_META.requested;
  const isActive = appt.status !== 'cancelled';
  return (
    <div
      className={`flex items-center gap-3 p-3 border rounded-lg ${isActive ? 'bg-white' : 'bg-slate-50 opacity-70'}`}
      data-testid={`lead-appt-row-${appt.appointment_id}`}
    >
      <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center flex-shrink-0">
        <Calendar size={16} className="text-purple-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-slate-900">
            {formatLocal(appt.scheduled_at, appt.timezone)}
          </span>
          <Badge variant="outline" className={`${meta.color} border font-normal text-[10px]`}>
            {meta.label}
          </Badge>
          <span className="text-xs text-slate-500 flex items-center gap-1">
            <Clock size={11} /> {appt.duration_minutes} min
          </span>
          {appt.origin === 'agent_manual' && (
            <Badge variant="outline" className="text-[10px]">Agent-scheduled</Badge>
          )}
        </div>
        {appt.visitor?.notes && (
          <div className="text-xs text-slate-600 mt-1 line-clamp-2">{appt.visitor.notes}</div>
        )}
        <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-2 flex-wrap">
          {appt.visitor?.phone && <span className="flex items-center gap-1"><Phone size={10} /> {formatPhone(appt.visitor.phone)}</span>}
          {appt.visitor?.email && <span className="flex items-center gap-1"><Mail size={10} /> {appt.visitor.email}</span>}
        </div>
      </div>
      {isActive && (
        <div className="flex items-center gap-1 flex-shrink-0">
          {appt.status !== 'confirmed' && onConfirm && (
            <button
              type="button"
              onClick={onConfirm}
              title="Confirm"
              className="p-1.5 rounded hover:bg-emerald-50 text-emerald-600"
              data-testid={`lead-appt-confirm-${appt.appointment_id}`}
            >
              <CheckCircle2 size={15} />
            </button>
          )}
          {onReschedule && (
            <button
              type="button"
              onClick={onReschedule}
              title="Reschedule"
              className="p-1.5 rounded hover:bg-amber-50 text-amber-600"
              data-testid={`lead-appt-reschedule-${appt.appointment_id}`}
            >
              <RefreshCw size={15} />
            </button>
          )}
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              title="Cancel"
              className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
              data-testid={`lead-appt-cancel-${appt.appointment_id}`}
            >
              <XCircle size={15} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function CreateAppointmentDialog({ open, onOpenChange, lead, onCreated }) {
  const [when, setWhen] = useState('');
  const [duration, setDuration] = useState('30');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const tz = useMemo(() => browserTz(), []);

  // Default the datetime picker to "tomorrow at 10:00" in local time.
  useEffect(() => {
    if (!open) return;
    setWhen(defaultWhen());
    setDuration('30');
    setNotes('');
  }, [open]);

  const submit = async () => {
    if (!when) { toast.error('Pick a date & time'); return; }
    const localDate = new Date(when);
    if (Number.isNaN(localDate.getTime())) { toast.error('Invalid date & time'); return; }
    if (localDate.getTime() < Date.now() - 60_000) {
      toast.error('That time is in the past');
      return;
    }
    setSaving(true);
    try {
      const r = await appointmentsAPI.createForLead({
        lead_id: lead.lead_id,
        scheduled_at: localDate.toISOString(),
        duration_minutes: parseInt(duration, 10),
        timezone: tz,
        notes: notes.trim() || undefined,
      });
      if (r.data?.visitor_email_sent) {
        toast.success('Appointment created — confirmation email sent to the lead');
      } else {
        toast.success('Appointment created (no email on file, so no confirmation was sent)');
      }
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create appointment');
    } finally {
      setSaving(false);
    }
  };

  const hasEmail = !!(lead.email || '').trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="lead-appt-create-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar size={16} className="text-purple-600" />
            Create Appointment
          </DialogTitle>
          <DialogDescription>
            Schedule a call with {lead.first_name} {lead.last_name || ''}. Appointment
            appears on your calendar and syncs to Google Calendar if connected.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {!hasEmail && (
            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              <span>
                This lead has no email address on file. The appointment will still
                be created and synced to your calendar, but no confirmation email
                will be sent to the lead.
              </span>
            </div>
          )}
          <div>
            <Label className="text-xs">Date &amp; Time *</Label>
            <DateTimeField
              value={when}
              onChange={setWhen}
              testId="lead-appt-when"
            />
            <p className="text-[10px] text-slate-500 mt-1">Timezone: {tz}</p>
          </div>
          <div>
            <Label className="text-xs">Duration</Label>
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger data-testid="lead-appt-duration"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="15">15 minutes</SelectItem>
                <SelectItem value="30">30 minutes</SelectItem>
                <SelectItem value="45">45 minutes</SelectItem>
                <SelectItem value="60">60 minutes</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Agenda, product to discuss, follow-ups…"
              data-testid="lead-appt-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="lead-appt-submit">
            {saving ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
            Create Appointment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RescheduleDialog({ open, onOpenChange, appt, newIso, setNewIso, onSubmit }) {
  if (!appt) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="lead-appt-reschedule-dialog">
        <DialogHeader>
          <DialogTitle>Reschedule Appointment</DialogTitle>
          <DialogDescription>
            Pick a new date and time. The linked calendar event will update and the visitor
            will be notified by email if they have one on file.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-xs text-slate-600">
            Current time: <b>{formatLocal(appt.scheduled_at, appt.timezone)}</b>
          </p>
          <div>
            <Label className="text-xs">New date &amp; time</Label>
            <DateTimeField
              value={newIso}
              onChange={setNewIso}
              testId="lead-appt-reschedule-when"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSubmit} data-testid="lead-appt-reschedule-submit">
            Reschedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// datetime-local value for tomorrow at 10:00 in local time.
function defaultWhen() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(10, 0, 0, 0);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
